import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiDownload, FiChevronDown, FiEye } from 'react-icons/fi'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import MetricCard from '@/components/ui/MetricCard'
import StatusBadge from '@/components/ui/StatusBadge'
import {
  ChartSkeleton,
  ListCardSkeleton,
  NotificationSkeleton,
  Skeleton,
  TableSkeleton,
} from '@/components/ui/Skeleton'
import NewShipmentModal from '../shipments/NewShipmentModal'
import EditShipmentModal from '../shipments/EditShipmentModal'
import ShipmentDetailsModal from '@/components/ui/ShipmentDetailsModal'
import {
  useDashboardStats,
  useRecentShipments,
  useRecentDocuments,
  useInventoryStatus,
  useNotifications,
  useDeliveredTonnage,
} from '@/hooks/useDashboard'
import { formatDateToDDMMMYYYY } from '@/utils/dateFormat'
import { SHIPMENT_STATUS_LABELS, shipmentToDisplay, type ShipmentDisplay } from '@/services/api/shipments'
import { openDocumentById, downloadDocumentById } from '@/services/api/documents'
import { ROUTES } from '@/constants/routes'

const Dashboard = () => {
  const navigate = useNavigate()
  const [isNewShipmentModalOpen, setIsNewShipmentModalOpen] = useState(false)
  const [isEditShipmentModalOpen, setIsEditShipmentModalOpen] = useState(false)
  const [isShipmentDetailsModalOpen, setIsShipmentDetailsModalOpen] = useState(false)
  const [selectedShipment, setSelectedShipment] = useState<ShipmentDisplay | null>(null)
  // Fetch data from API
  const { stats, loading: statsLoading } = useDashboardStats()
  const { shipments: recentShipments, loading: shipmentsLoading } = useRecentShipments(5, 'active')
  const { documents: recentDocuments, loading: documentsLoading } = useRecentDocuments(5)
  const { lots: inventoryLots, loading: inventoryLoading } = useInventoryStatus(5)
  const { notifications, loading: notificationsLoading } = useNotifications(false, 1, 4)
  const { data: deliveredData, loading: deliveredLoading } = useDeliveredTonnage()
  // No delete on dashboard — demo-safe (Claude / Marc exec review)

  // Format trend text — hide meaningless 0% / ±100% (Claude exec review)
  const formatTrend = (value: number, label: string) => {
    if (!Number.isFinite(value) || Math.abs(value) < 0.5) return '—'
    if (Math.abs(value) >= 99.5) return '—'
    const sign = value >= 0 ? '↑' : '↓'
    return `${sign} ${Math.round(Math.abs(value))}% ${label}`
  }

  // Format currency (South African Rand — matches Finance / Xero)
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      currencyDisplay: 'code',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  const formatCount = (value: number) =>
    new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 0 }).format(value)

  const formatWeightTon = (value: number) => {
    const safeKg = Number.isFinite(value) ? value : 0
    const tons = safeKg / 1000
    // Whole tonnes when within 0.05 of an integer (avoids 13,969.71 vs 13,970 noise)
    const rounded = Math.abs(tons - Math.round(tons)) < 0.05 ? Math.round(tons) : tons
    const digits = Number.isInteger(rounded) ? 0 : 1
    return `${new Intl.NumberFormat('en-ZA', { maximumFractionDigits: digits }).format(rounded)} tonnes`
  }

  const handleViewDocument = async (documentId: string) => {
    try {
      await openDocumentById(documentId)
    } catch {
      window.alert('Could not open document. Please try again or download it instead.')
    }
  }

  const handleDownloadDocument = async (documentId: string, name: string) => {
    try {
      await downloadDocumentById(documentId, name)
    } catch {
      window.alert('Could not download document. Please try again.')
    }
  }

  const titleCasePlace = (value: string | null | undefined) => {
    if (!value) return 'N/A'
    return value
      .toLowerCase()
      .split(/([\s/-]+)/)
      .map((part) => (/^[\s/-]+$/.test(part) ? part : part.charAt(0).toUpperCase() + part.slice(1)))
      .join('')
  }

  const formatDocumentDate = (date: Date | string | undefined) => {
    if (!date) return '—'
    const d = new Date(date)
    if (Number.isNaN(d.getTime())) return '—'
    return formatDateToDDMMMYYYY(d.toISOString())
  }

  // Format time ago
  const formatTimeAgo = (date: Date | string | undefined) => {
    if (!date) return 'recently'
    const now = new Date()
    const past = new Date(date)
    if (Number.isNaN(past.getTime())) return 'recently'
    const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000)
    if (diffInSeconds < 0) return 'Just now'

    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`
    if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`
    return `${Math.floor(diffInSeconds / 31536000)} years ago`
  }

  // Get notification color based on type
  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'bg-green-500'
      case 'warning':
        return 'bg-yellow-500'
      case 'error':
        return 'bg-red-500'
      default:
        return 'bg-blue-500'
    }
  }

  // Get stock level color and label
  const getStockLevelInfo = (stockLevel: string) => {
    switch (stockLevel) {
      case 'critical':
        return { color: '#E93323', bgColor: '#FDEBE9', label: 'Critical', width: 20 }
      case 'low':
        return { color: '#E93323', bgColor: '#FDEBE9', label: 'Low Stock', width: 40 }
      case 'medium':
        return { color: '#EEC233', bgColor: '#FEF9EB', label: 'Moderate', width: 60 }
      case 'high':
        return { color: '#13AB5B', bgColor: '#E8F7EF', label: 'Sufficient', width: 85 }
      default:
        return { color: '#69697A', bgColor: '#F2F4F2', label: 'Unknown', width: 50 }
    }
  }

  // Map raw shipment status to a human-readable label (shared with shipments)
  const getStatusLabel = (status: string): string => {
    const normalized = status?.toLowerCase().trim() || ''
    return SHIPMENT_STATUS_LABELS[normalized] ?? status
  }

  // Map database shipment status to StatusBadge-compatible props.
  // If DB says arrived but ETA/ETD is still in the future, show In transit (exec review).
  const getStatusBadgeProps = (
    status: string,
    shipment?: { eta?: Date | string | null; etd?: Date | string | null },
  ): { status: 'in_transit' | 'delayed' | 'arrived' | 'pending' | 'completed'; label: string } => {
    const normalized = status?.toLowerCase() || ''
    const now = Date.now()
    const etaMs = shipment?.eta ? new Date(shipment.eta).getTime() : NaN
    const etdMs = shipment?.etd ? new Date(shipment.etd).getTime() : NaN
    const stillAhead =
      (Number.isFinite(etaMs) && etaMs > now) || (Number.isFinite(etdMs) && etdMs > now)

    if ((normalized === 'arrived' || normalized === 'fully_delivered') && stillAhead) {
      return { status: 'in_transit', label: 'In transit' }
    }

    switch (normalized) {
      case 'in_transit':
        return { status: 'in_transit', label: 'In transit' }
      case 'delayed':
        return { status: 'delayed', label: 'Delayed' }
      case 'arrived':
        return { status: 'arrived', label: 'Arrived' }
      case 'cleared':
      case 'unpacked':
      case 'in_storage':
      case 'fully_delivered':
        return { status: 'completed', label: getStatusLabel(normalized) }
      case 'pending':
      case 'in_coordination':
      default:
        return { status: 'pending', label: getStatusLabel(normalized) }
    }
  }

  const escapeCsv = (value: unknown): string => {
    const text = value == null ? '' : String(value)
    if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`
    return text
  }

  const downloadCsv = (filename: string, headers: string[], rows: Array<Array<unknown>>) => {
    const csv = [headers.join(','), ...rows.map((row) => row.map(escapeCsv).join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const exportActiveShipments = () => {
    downloadCsv(
      `dashboard-active-shipments-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Container Number', 'Shipping Line', 'Origin', 'Destination', 'ETD', 'ETA', 'Status'],
      recentShipments.map((shipment) => [
        shipment.containerNumber || 'N/A',
        shipment.carrier || 'N/A',
        shipment.pol || 'N/A',
        shipment.pod || 'N/A',
        shipment.etd ? formatDateToDDMMMYYYY(shipment.etd.toString()) : 'N/A',
        shipment.eta ? formatDateToDDMMMYYYY(shipment.eta.toString()) : 'N/A',
        getStatusLabel(shipment.status),
      ])
    )
  }

  const exportDeliveredTonnage = () => {
    downloadCsv(
      `dashboard-delivered-tonnage-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Customer', 'Tonnes', 'Share', 'Notes'],
      (deliveredData?.byCustomer || []).map((row) => [
        row.customer,
        row.tonnes,
        row.share != null ? `${(row.share * 100).toFixed(1)}%` : '',
        row.notes || '',
      ])
    )
  }

  const exportRecentDocuments = () => {
    downloadCsv(
      `dashboard-recent-documents-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Name', 'Uploaded By', 'Uploaded At', 'URL'],
      recentDocuments.map((doc) => [doc.name, doc.user?.name || doc.user?.email || 'N/A', doc.uploadedAt, doc.url])
    )
  }

  const exportInventoryStatus = () => {
    downloadCsv(
      `dashboard-inventory-status-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Lot Number', 'SKU', 'Location', 'Units', 'Weight (kg)', 'Stock Level', 'Updated At'],
      inventoryLots.map((lot) => [
        lot.lotNumber,
        lot.sku || 'N/A',
        lot.location || 'N/A',
        lot.bags ?? lot.pieces ?? lot.balance ?? 0,
        lot.weight ?? '',
        lot.stockLevel,
        lot.updatedAt,
      ])
    )
  }

  return (
    <div className="space-y-6" style={{ maxWidth: '100%' }}>
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1
            className="text-lg sm:text-xl"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              lineHeight: '32px',
              letterSpacing: '0%',
              color: '#212126'
            }}
          >
            Dashboard
          </h1>
          <p
            className="mt-1 text-xs sm:text-sm"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 400,
              fontStyle: 'normal',
              lineHeight: '20px',
              letterSpacing: '0%',
              color: '#69697A'
            }}
          >
            Operations overview
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-3">
          {/* Export Button */}
          <button
            onClick={exportActiveShipments}
            className="flex items-center justify-center gap-2 sm:gap-3 font-medium rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap w-full sm:w-auto"
            style={{
              height: '38px',
              color: '#212126',
              border: '1px solid #212126',
              padding: '8px 20px',
              borderRadius: '6px',
              minWidth: '100px'
            }}
          >
            <img
              src="/images/download.svg"
              alt="Download"
              className="w-4 h-4"
              style={{
                filter: 'brightness(0) saturate(100%) invert(8%) sepia(4%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(95%)'
              }}
            />
            <span className="text-sm sm:text-base">Export</span>
          </button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-4"
        style={{
          width: '100%'
        }}
      >
        <MetricCard
          title="Active shipments"
          value={stats ? stats.activeShipments.toString() : '0'}
          trend={stats ? formatTrend(stats.activeShipmentsTrend, 'from last month') : ''}
          loading={statsLoading && !stats}
          icon={<img src="/images/ship.svg" alt="" className="w-6 h-6" />}
          iconBgColor="bg-gray-100"
          iconBgColorCustom="#F2F4F2"
          style={{ width: '100%', minHeight: '120px', height: 'auto' }}
          onClick={() => navigate(ROUTES.SHIPMENTS)}
          description="Opens Shipments"
        />
        <MetricCard
          title="Clearing status"
          value={stats ? stats.pendingDocuments.toString() : '0'}
          trend={stats ? formatTrend(stats.pendingDocumentsTrend, 'from last month') : ''}
          loading={statsLoading && !stats}
          icon={<img src="/images/pending-document.svg" alt="" className="w-6 h-6" />}
          iconBgColor="bg-gray-100"
          iconBgColorCustom="#F2F4F2"
          style={{ width: '100%', minHeight: '120px', height: 'auto' }}
          onClick={() => navigate(ROUTES.CLEARING)}
          description="Opens Clearing. Metric still uses pending uploads until Marc defines the clearing KPI."
        />
        <MetricCard
          title="Current stock"
          value={stats ? formatWeightTon(stats.stockCurrentWeight ?? 0) : formatWeightTon(0)}
          trend="On hand"
          loading={statsLoading && !stats}
          icon={<img src="/images/stockLevel.svg" alt="" className="w-5 h-5" />}
          iconBgColor="bg-gray-100"
          iconBgColorCustom="#F2F4F2"
          style={{ width: '100%', minHeight: '120px', height: 'auto' }}
          onClick={() => navigate(ROUTES.INVENTORY)}
          description="Opens Storage & Inventory"
        />
        <MetricCard
          title="Stock YTD"
          value={stats ? formatWeightTon(stats.stockYtdWeight ?? stats.stockTotalWeight ?? 0) : formatWeightTon(0)}
          trend="From 1 Apr"
          loading={statsLoading && !stats}
          icon={<img src="/images/stockLevel.svg" alt="" className="w-5 h-5" />}
          iconBgColor="bg-gray-100"
          iconBgColorCustom="#F2F4F2"
          style={{ width: '100%', minHeight: '120px', height: 'auto' }}
          onClick={() => navigate(ROUTES.INVENTORY)}
          description="Opens Storage & Inventory"
        />
        <MetricCard
          title="Bags YTD"
          value={stats ? formatCount(stats.stockYtdBags ?? 0) : '0'}
          trend="Barcode units"
          loading={statsLoading && !stats}
          icon={<img src="/images/stockLevel.svg" alt="" className="w-5 h-5" />}
          iconBgColor="bg-gray-100"
          iconBgColorCustom="#F2F4F2"
          style={{ width: '100%', minHeight: '120px', height: 'auto' }}
          onClick={() => navigate(ROUTES.INVENTORY)}
          description="Opens Storage & Inventory"
        />
        <MetricCard
          title="Pending payments"
          value={stats ? formatCurrency(stats.pendingPayments) : formatCurrency(0)}
          trend={stats ? formatTrend(stats.pendingPaymentsTrend, 'from last month') : ''}
          loading={statsLoading && !stats}
          icon={<img src="/images/dollar-circle.svg" alt="" style={{ width: '20px', height: '20px' }} />}
          iconBgColor="bg-gray-100"
          iconBgColorCustom="#F2F4F2"
          style={{ width: '100%', minHeight: '120px', height: 'auto' }}
          onClick={() => navigate(ROUTES.FINANCE_OVERVIEW)}
          description="Xero awaiting payment · opens Finance"
        />
      </div>

      {/* Tonnes delivered YTD per customer (replaces Contract Completion) + notifications */}
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 w-full lg:w-[calc(75%-12px)]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h3
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontStyle: 'normal',
                  fontSize: 'clamp(13px, 3vw, 14px)',
                  lineHeight: '28px',
                  letterSpacing: '0%',
                  color: '#212126',
                }}
              >
                Tonnes delivered by customer
              </h3>
              <p
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '18px',
                  color: '#69697A',
                }}
              >
                {deliveredData?.periodLabel
                  ? deliveredData.periodLabel
                  : 'Weekly closing matrix'}
              </p>
            </div>
            <button
              onClick={exportDeliveredTonnage}
              className="flex items-center justify-center gap-1 hover:opacity-90 transition-opacity whitespace-nowrap"
              style={{
                width: '65px',
                height: '34px',
                borderRadius: '6px',
                paddingTop: '8px',
                paddingRight: '12px',
                paddingBottom: '8px',
                paddingLeft: '12px',
                backgroundColor: '#212126',
                opacity: 1,
              }}
            >
              <FiDownload className="w-4 h-4" style={{ color: '#FFFFFF' }} />
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '20px',
                  letterSpacing: '0%',
                  color: '#FFFFFF',
                }}
              >
                Export
              </span>
            </button>
          </div>
          <div className="mb-4 flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-8">
            <div className="flex flex-col">
              <p
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: 'clamp(18px, 4vw, 20px)',
                  lineHeight: '32px',
                  color: '#69697A',
                }}
              >
                {deliveredLoading && !deliveredData ? (
                  <Skeleton style={{ width: 72, height: 24 }} />
                ) : (
                  `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(deliveredData?.totalTonnes ?? 0)} t`
                )}
              </p>
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#13AB5B',
                }}
              >
                Total delivered
              </span>
            </div>
            <div className="flex flex-col">
              <p
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: 'clamp(18px, 4vw, 20px)',
                  lineHeight: '32px',
                  color: '#69697A',
                }}
              >
                {deliveredLoading && !deliveredData ? (
                  <Skeleton style={{ width: 48, height: 24 }} />
                ) : (
                  formatCount(deliveredData?.customerCount ?? 0)
                )}
              </p>
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#69697A',
                }}
              >
                Customers
              </span>
            </div>
            <div className="flex flex-col">
              <p
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: 'clamp(18px, 4vw, 20px)',
                  lineHeight: '32px',
                  color: '#69697A',
                }}
              >
                {deliveredLoading && !deliveredData ? (
                  <Skeleton style={{ width: 72, height: 24 }} />
                ) : (
                  `${(((deliveredData?.pctOfTarget ?? 0) * 100)).toFixed(1)}%`
                )}
              </p>
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#69697A',
                }}
              >
                Of {new Intl.NumberFormat('en-US').format(deliveredData?.targetMt ?? 12000)} t target
              </span>
            </div>
          </div>
          <div className="w-full" style={{ height: 'clamp(250px, 40vw, 300px)' }}>
            {deliveredLoading && !deliveredData ? (
              <ChartSkeleton />
            ) : (deliveredData?.chartData?.length ?? 0) === 0 ? (
              <div className="flex h-full items-center justify-center text-sm" style={{ color: '#69697A', fontFamily: 'Lexend Deca' }}>
                No tonnage matrix loaded yet. Import KCG_Tonnages_To_Date workbook on the server.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={deliveredData?.chartData || []} margin={{ bottom: 48, left: 8, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal vertical={false} />
                  <XAxis
                    dataKey="customer"
                    stroke="#6B7280"
                    axisLine={{ stroke: '#E9E9EA', strokeWidth: 1 }}
                    tickLine={false}
                    interval={0}
                    angle={-28}
                    textAnchor="end"
                    height={60}
                    tick={{
                      fill: '#050521',
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontSize: '11px',
                    }}
                  />
                  <YAxis
                    stroke="#6B7280"
                    tick={{
                      fill: '#69697A',
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontSize: '12px',
                    }}
                    tickFormatter={(value) => `${Number(value)}`}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    formatter={(value: number | string) => [
                      `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(Number(value))} tonnes`,
                      'Delivered',
                    ]}
                  />
                  <Bar dataKey="tonnes" fill="#2C71F6" name="Tonnes" radius={[8, 8, 0, 0]} barSize={16} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Recent Notifications - 25% width (1 metric card), aligned below Pending Payments */}
        <div
          className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 w-full lg:w-[calc(25%-12px)]"
        >
          <h2
            className="mb-4 text-sm sm:text-base"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              lineHeight: '28px',
              letterSpacing: '0%',
              color: '#212126'
            }}
          >
            Recent Notifications
          </h2>
          {/* Full-width separator line - covers entire card width */}
          <div
            style={{
              position: 'relative',
              width: 'calc(100% + 48px)',
              height: '0px',
              borderTop: '1px solid #E9E9EA',
              marginBottom: '16px',
              marginLeft: '-24px',
              marginRight: '-24px',
              left: '0',
              right: '0'
            }}
          ></div>
          <div>
            {notificationsLoading && notifications.length === 0 ? (
              <NotificationSkeleton rows={4} />
            ) : notifications.length === 0 ? (
              <div className="text-center py-4 text-gray-500">No notifications</div>
            ) : (
              notifications.map((notification, index) => (
                <div key={notification.id}>
                  {index > 0 && (
                    <div
                      style={{
                        width: '100%',
                        height: '0px',
                        borderTop: '1px solid #E9E9EA',
                        marginBottom: '16px'
                      }}
                    ></div>
                  )}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (notification.link) navigate(notification.link)
                    }}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && notification.link) {
                        e.preventDefault()
                        navigate(notification.link!)
                      }
                    }}
                    className={`flex items-start gap-3 ${index < notifications.length - 1 ? 'pb-4' : ''} ${notification.link ? 'cursor-pointer hover:bg-gray-50 rounded-md -mx-1 px-1 py-1 transition-colors' : ''}`}
                  >
                    <div className={`w-2 h-2 rounded-full ${getNotificationColor(notification.type)} mt-2 flex-shrink-0`}></div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">
                        {notification.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{formatTimeAgo(notification.createdAt)}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Active Shipments Table - Full Width */}
      <div
        className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 w-full"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <h2
            className="text-sm sm:text-base"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              lineHeight: '28px',
              letterSpacing: '0%',
              color: '#212126'
            }}
          >
            Active Shipments
          </h2>
          <div className="relative w-full sm:w-auto">
            <img
              src="/images/calendar.svg"
              alt="Calendar"
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none"
            />
            <select className="appearance-none bg-white border border-gray-300 rounded-lg pl-10 pr-8 py-2 text-sm text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full sm:w-auto">
              <option>Month</option>
              <option>January</option>
              <option>February</option>
              <option>March</option>
              <option>April</option>
              <option>May</option>
              <option>June</option>
              <option>July</option>
              <option>August</option>
              <option>September</option>
              <option>October</option>
              <option>November</option>
              <option>December</option>
            </select>
            <FiChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
          </div>
        </div>
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle px-4 sm:px-0">
            <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'auto', width: '100%', minWidth: '800px' }}>
              <thead>
                <tr>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 border-l border-t border-b border-gray-200" style={{ width: 'auto', minWidth: '120px', maxWidth: '150px', borderTopLeftRadius: '8px' }}>Container Number</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 border-t border-b border-gray-200" style={{ width: 'auto', minWidth: '110px', maxWidth: '140px' }}>Shipping Line</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 border-t border-b border-gray-200" style={{ width: 'auto', minWidth: '90px', maxWidth: '120px' }}>Origin</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 border-t border-b border-gray-200" style={{ width: 'auto', minWidth: '110px', maxWidth: '140px' }}>Destination</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 whitespace-nowrap border-t border-b border-gray-200" style={{ width: 'auto', minWidth: '100px' }}>ETD</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 whitespace-nowrap border-t border-b border-gray-200" style={{ width: 'auto', minWidth: '100px' }}>ETA</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 border-t border-b border-gray-200" style={{ width: 'auto', minWidth: '100px' }}>Status</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 border-r border-t border-b border-gray-200" style={{ width: 'auto', minWidth: '100px', paddingLeft: '24px', borderTopRightRadius: '8px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {shipmentsLoading && recentShipments.length === 0 ? (
                  <TableSkeleton rows={5} cols={8} />
                ) : recentShipments.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-gray-500 border-b border-gray-200">
                      No active shipments found
                    </td>
                  </tr>
                ) : (
                  recentShipments.map((shipment, index) => {
                    const shipmentData = shipmentToDisplay({
                      id: shipment.id,
                      booking: shipment.booking,
                      bl: shipment.bl,
                      billOfLading: shipment.bl,
                      containerNumber: shipment.containerNumber,
                      vessel: shipment.vessel,
                      carrier: shipment.carrier,
                      pol: shipment.pol,
                      pod: shipment.pod,
                      etd: shipment.etd?.toString(),
                      eta: shipment.eta?.toString(),
                      status: shipment.status,
                    })

                    const isLastRow = index === recentShipments.length - 1

                    return (
                      <tr key={shipment.id} className="hover:bg-gray-50">
                        <td
                          className="py-3 px-2 text-sm text-gray-900 border-l border-b border-gray-200"
                          style={{
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '150px',
                            ...(isLastRow ? { borderBottomLeftRadius: '8px' } : {})
                          }}
                        >
                          {shipment.containerNumber || 'N/A'}
                        </td>
                        <td
                          className="py-3 px-2 text-sm text-gray-600 border-b border-gray-200"
                          style={{
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '140px'
                          }}
                        >
                          {shipment.carrier || 'N/A'}
                        </td>
                        <td
                          className="py-3 px-2 text-sm text-gray-600 border-b border-gray-200"
                          style={{
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '120px'
                          }}
                        >
                          {titleCasePlace(shipment.pol)}
                        </td>
                        <td
                          className="py-3 px-2 text-sm text-gray-600 border-b border-gray-200"
                          style={{
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '140px'
                          }}
                        >
                          {titleCasePlace(shipment.pod)}
                        </td>
                        <td className="py-3 px-2 text-sm text-gray-600 whitespace-nowrap border-b border-gray-200">
                          {shipment.etd ? formatDateToDDMMMYYYY(shipment.etd.toString()) : 'N/A'}
                        </td>
                        <td className="py-3 px-2 text-sm text-gray-600 whitespace-nowrap border-b border-gray-200">
                          {shipment.eta ? formatDateToDDMMMYYYY(shipment.eta.toString()) : 'N/A'}
                        </td>
                        <td className="py-3 px-2 border-b border-gray-200">
                          {(() => {
                            const statusBadgeProps = getStatusBadgeProps(shipment.status, shipment)
                            return <StatusBadge status={statusBadgeProps.status} label={statusBadgeProps.label} />
                          })()}
                        </td>
                        <td className="py-3 px-2 border-r border-b border-gray-200" style={{ paddingLeft: '24px', ...(isLastRow ? { borderBottomRightRadius: '8px' } : {}) }}>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setSelectedShipment(shipmentData)
                                setIsEditShipmentModalOpen(true)
                              }}
                              className="p-1 transition-all flex items-center justify-center cursor-pointer hover:opacity-80 hover:shadow-sm"
                              style={{
                                backgroundColor: '#FEF9EB',
                                width: '24px',
                                height: '24px',
                                borderRadius: '4px'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#F5E6C8'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#FEF9EB'
                              }}
                            >
                              <img src="/images/linearDots.svg" alt="More options" className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedShipment(shipmentData)
                                setIsShipmentDetailsModalOpen(true)
                              }}
                              className="p-1 transition-all flex items-center justify-center cursor-pointer hover:opacity-80 hover:shadow-sm"
                              style={{
                                backgroundColor: '#EAF1FF',
                                width: '24px',
                                height: '24px',
                                borderRadius: '4px'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#D4E3FF'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#EAF1FF'
                              }}
                            >
                              <img src="/images/eye.svg" alt="View" className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Bottom Section - Recent Documents and Inventory Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Documents */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h2
              className="text-sm sm:text-base"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontStyle: 'normal',
                lineHeight: '28px',
                letterSpacing: '0%',
                color: '#212126'
              }}
            >
              Recent Documents
            </h2>
            <button
              onClick={exportRecentDocuments}
              className="flex items-center justify-center gap-2 hover:opacity-90 transition-opacity w-full sm:w-auto"
              style={{
                height: '34px',
                borderRadius: '6px',
                paddingTop: '8px',
                paddingRight: '12px',
                paddingBottom: '8px',
                paddingLeft: '12px',
                backgroundColor: '#212126',
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontSize: '12px',
                lineHeight: '20px',
                letterSpacing: '0%',
                color: '#FFFFFF',
                minWidth: '65px'
              }}
            >
              <FiDownload className="w-4 h-4" style={{ color: '#FFFFFF' }} />
              <span>Export</span>
            </button>
          </div>
          <div className="space-y-4">
            {documentsLoading && recentDocuments.length === 0 ? (
              <ListCardSkeleton rows={4} />
            ) : recentDocuments.length === 0 ? (
              <div className="text-center py-4 text-gray-500">No recent documents</div>
            ) : (
              recentDocuments.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => void handleViewDocument(doc.id)}
                      className="p-0 border-0 bg-transparent cursor-pointer rounded flex items-center justify-center text-gray-600 hover:text-gray-900"
                      title="View document"
                    >
                      <FiEye className="w-5 h-5" style={{ color: '#2C71F6' }} />
                    </button>
                    <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                      <img src="/images/clearing-status.svg" alt="Document" className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {doc.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Added {formatDocumentDate(doc.uploadedAt)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => void handleDownloadDocument(doc.id, doc.name)}
                      className="p-2 text-gray-600 hover:text-gray-900 rounded flex items-center justify-center"
                      style={{
                        backgroundColor: '#E8F7EF'
                      }}
                      title="Download document"
                    >
                      <img src="/images/download.svg" alt="Download" className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Inventory Status */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h2
              className="text-sm sm:text-base"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontStyle: 'normal',
                lineHeight: '28px',
                letterSpacing: '0%',
                color: '#212126'
              }}
            >
              Inventory Status
            </h2>
            <button
              onClick={exportInventoryStatus}
              className="flex items-center justify-center gap-2 hover:opacity-90 transition-opacity w-full sm:w-auto"
              style={{
                height: '34px',
                borderRadius: '6px',
                paddingTop: '8px',
                paddingRight: '12px',
                paddingBottom: '8px',
                paddingLeft: '12px',
                backgroundColor: '#212126',
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontSize: '12px',
                lineHeight: '20px',
                letterSpacing: '0%',
                color: '#FFFFFF',
                minWidth: '65px'
              }}
            >
              <FiDownload className="w-4 h-4" style={{ color: '#FFFFFF' }} />
              <span>Export</span>
            </button>
          </div>
          <div className="space-y-4">
            {inventoryLoading && inventoryLots.length === 0 ? (
              <ListCardSkeleton rows={4} />
            ) : inventoryLots.length === 0 ? (
              <div className="text-center py-4 text-gray-500">No stock level alerts</div>
            ) : (
              inventoryLots.map((lot) => {
                const stockInfo = getStockLevelInfo(lot.stockLevel)
                const units = lot.bags ?? lot.pieces ?? lot.balance ?? 0

                return (
                  <div key={lot.id} className="p-3 border border-gray-100 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FEF4F3' }}>
                          <img src="/images/box.svg" alt="Box" className="w-5 h-5" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">{lot.lotNumber}</span>
                          <span className="text-xs text-gray-500 mt-1">{lot.sku || 'N/A'}{lot.location ? ` · ${lot.location}` : ''}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end flex-1 ml-4">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-24 bg-gray-200 rounded-full h-1.5">
                            <div
                              className="h-1.5 rounded-full"
                              style={{
                                width: `${stockInfo.width}%`,
                                backgroundColor: stockInfo.color
                              }}
                            ></div>
                          </div>
                          <div className="flex flex-col items-start">
                            <span className="text-sm text-gray-600">{units} Units{lot.weight ? ` · ${lot.weight} kg` : ''}</span>
                            <span
                              className="px-2 py-1 rounded mt-1"
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 300,
                                fontStyle: 'normal',
                                fontSize: '12px',
                                lineHeight: '16px',
                                letterSpacing: '0%',
                                color: stockInfo.color,
                                backgroundColor: stockInfo.bgColor
                              }}
                            >
                              {stockInfo.label}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>

      {/* New Shipment Modal */}
      <NewShipmentModal
        isOpen={isNewShipmentModalOpen}
        onClose={() => setIsNewShipmentModalOpen(false)}
      />

      {/* Edit Shipment Modal */}
      <EditShipmentModal
        isOpen={isEditShipmentModalOpen}
        onClose={() => {
          setIsEditShipmentModalOpen(false)
          setSelectedShipment(null)
        }}
        shipmentData={selectedShipment || undefined}
      />

      {/* Shipment Details Modal */}
      <ShipmentDetailsModal
        isOpen={isShipmentDetailsModalOpen}
        onClose={() => {
          setIsShipmentDetailsModalOpen(false)
          setSelectedShipment(null)
        }}
        shipmentData={selectedShipment || undefined}
      />
    </div>
  )
}

export default Dashboard
